import moment from 'moment'
import 'moment/locale/de'
import 'moment/locale/en-gb'
import 'moment/locale/fr'
/**
 * @summary Get branch statistic: total, customers, average check and cash registers
 * @param {Object} shop shop from region
 * @param {String} formatedDate active date string
 * @returns {Array} { key: String, value: String }
 */
export const getStats = ({ bills = [], cashRegisters = [] }, formatedDate) => {
	const filteredBills = bills.filter(bill => {
		const { dateTime, isCancelled, isDeleted, creditClientValue, total } = bill
		return moment(dateTime).format('YYYY-MM-DD') === formatedDate &&
			(!isCancelled && !isDeleted && creditClientValue === 0 && total > 0)
	})
	const totalValue = filteredBills.reduce((acc, { total }) => acc + total, 0),
		cashRegistersCount = cashRegisters.length,
		customerCount = filteredBills.length,
		averageCount = totalValue / customerCount
	return [
		{
			key: 'total',
			value: `${totalValue.toFixed(2)} CHF`
		},
		{
			key: 'customers',
			value: customerCount
		},
		{
			key: 'average',
			value: `${(isNaN(averageCount) ? 0 : averageCount).toFixed(2)} CHF`
		},
		{
			key: 'activeCR',
			value: cashRegistersCount
		}
	]
}

const setLocale = (lang) => {
	let ll = ''
	switch (lang) {
		case 'English': {
			ll = 'en-gb'
			break
		}
		case 'German': {
			ll = 'de'
			break
		}
		case 'French': {
			ll = 'fr'
			break
		}
		default: {
			ll = 'de'
			break
		}
	}
	moment.locale(ll) // set locale
}

export const getFrequencies = ({ stats, cashRegisters, currency, allowedCashRegisters }) => {
	return [
		{
			key: 'total',
			value: stats && stats[0] ? `${stats[0].total.toFixed(2)} ${currency}` : `0.00 ${currency}`
		},
		{
			key: 'customers',
			value: stats && stats[0] ? stats[0].billCount : 0
		},
		{
			key: 'average',
			value: stats && stats[0] ? `${stats[0].totalK.toFixed(2)} ${currency}` : `0.00 ${currency}`
		},
		{
			key: 'activeCR',
			value: {
				allowedCRs: allowedCashRegisters?.length ?? 0,
				allCRs: cashRegisters?.length
			}
		}
	]
}

export const getWeekdays = (lang) => {
	setLocale(lang)
	const startOfWeek = moment().startOf('isoWeek'),
		endOfWeek = moment().endOf('isoWeek')
	let days = [], day = startOfWeek
	while (day <= endOfWeek) {
		days.push(day.format('dddd'))
		day = day.clone().add(1, 'd')
	}
	return days
}

export const getIntervalDates = (formatedDate) => {
	const today = moment(formatedDate)
	// isoWeekday: Monday=1 … Sunday=7. Anchor the range to the current ISO week
	// so charts run Monday → Sunday (7 days), not Sunday → Sunday (8 days).
	const dayOfWeek = today.isoWeekday()
	const dateFrom = today.clone().subtract(dayOfWeek - 1, 'day').format('YYYY-MM-DD')
	const dateTo = today.clone().add(7 - dayOfWeek, 'days').format('YYYY-MM-DD')
	return { dateFrom, dateTo }
}

export const getComparisonDates = (dateFrom, dateTo, activeDate, t) => {
	const from = moment(dateFrom), to = moment(dateTo).add(1, 'days').format('YYYY-MM-DD')
	let currentDate = from, result = []
	const mActiveDate = moment(activeDate)
	// const weekLabels = { 2: 'two', 3: 'three' }
	do {
		result.push(
			{
				field: currentDate.format('YYYY-MM-DD'),
				label: currentDate.format('dddd')
			}
		)
		currentDate = currentDate.add(1, 'days')
	} while (!currentDate.isSame(to))
	return [
		...result,
		...Array.from(Array(3).keys()).map(quantity => {
			const q = quantity + 1
			return {
				field: mActiveDate
					.subtract(q, 'weeks')
					.format('YYYY-MM-DD'),
				label: `${t('dashboard_section.this_day')} (${mActiveDate.format('dddd')})` +
					`${q === 1 ? ` ${t('dashboard_section.week_ago')}` : ` ${t('dashboard_section.n_week_ago', { week: q })}`}`
			}
		})
	]
}

export const getForecastLines = t => {
	const prefix = 'dashboard_section.forecast.'
	return {
		very_pessimistic: { title: t(`${prefix}very_pessimistic`), color: '#CE3452' },
		pessimistic: { title: t(`${prefix}pessimistic`), color: '#DF634F' },
		most_possible: { title: t(`${prefix}most_possible`), color: '#6CB1C8' },
		optimistic: { title: t(`${prefix}optimistic`), color: '#F0C350' },
		very_optimistic: { title: t(`${prefix}very_optimistic`), color: '#60BB78' },
		history: { title: t(`${prefix}history`), color: '#B0B0B0' }
	}
}

export const getForecastSwitches = t => {
	const prefix = 'dashboard_section.forecast.'
	return {
		[t(`${prefix}very_pessimistic`)]: false,
		[t(`${prefix}pessimistic`)]: false,
		[t(`${prefix}most_possible`)]: true,
		[t(`${prefix}optimistic`)]: false,
		[t(`${prefix}very_optimistic`)]: false,
		[t(`${prefix}history`)]: true
	}
}

export const salesForecastingPrognoseTypes = [
	'very_pessimistic',
	'pessimistic',
	'most_possible',
	'optimistic',
	'very_optimistic',
	'history'
]

// WHEN USE dateType = 'YYYY-MM-DD'
// MAKE '14 Sep'
// FROM '2022-09-14'
// -------------------------------
// WHEN USE dateType = 'D MMMM'
// MAKE '14 Sep'
// FROM '14 September'
export const getCompactDate = (longDate, dateType = 'YYYY-MM-DD')=> {
	let date = longDate
	if(dateType === 'YYYY-MM-DD') {
		date = date.format('D MMMM')
	}
	const dateExp = new RegExp(/\d* \D\D\D/)
	if(date) {
		const compactDate = date.match(dateExp)[0]
		return compactDate
	}
}

export const getThreeFirstLetters = (str)=> {
	const threeLettersExp = new RegExp(/\D\D\D/)
	if(str) {
		const threeLetters = str.match(threeLettersExp)[0]
		return threeLetters
	}
}

export const clusterColors = {
	0: '#CA498C',
	1: '#CE3452',
	2: '#DF634F',
	3: '#F0C350',
	4: '#6CB1C8',
	5: '#387C89',
	6: '#4C485F',
	7: '#60BB78',
	8: '#96BEA1',
	9: '#D1BA7C'
}

export const clusterColorsRGB = {
	0: '240, 195, 80',
	1: '108, 177, 200',
	2: '56, 124, 137',
	3: '76, 72, 95',
	4: '202, 73, 140',
	5: '206, 52, 82',
	6: '223, 99, 79',
	7: '96, 187, 120',
	8: '150, 190, 161',
	9: '209, 186, 124'
}


export const getMonthes = (lang) => {
	setLocale(lang)
	return Array.apply(0, Array(12)).map(function (_, i) {
		return moment().month(i).format('MMMM')
	})
}

export const sizes = {
	mobile: 768,
	tablet: 1200
}

export const breakpoints = {
	mobile: `max-width: ${sizes.mobile}px`
}

export const weatherIcons = {
	'snow': 'day-snow',
	'rain': 'day-rain',
	'fog': 'day-fog',
	'wind': 'day-windy',
	'cloudy': 'cloudy',
	'partly-cloudy-day': 'day-cloudy',
	'partly-cloudy-night': 'night-partly-cloudy',
	'clear-day': 'day-sunny',
	'clear-night': 'night-clear'
}

export const weatheri18 = {
	'Rain': 'weather.rain',
	'Clear': 'weather.clear',
	'Rain, Partially cloudy': 'weather.rain_partially_cloudy',
	'Overcast': 'weather.overcast',
	'Partially cloudy': 'weather.partially_cloudy',
	'Snow, Overcast': 'weather.snow_overcast',
	'Rain, Overcast': 'weather.rain_overcast'
}

export const objectCheck = (obj) => typeof obj === 'object' && obj !== null && Object.values(obj).length
